import type { AgentEdge, AgentNode } from '@saaga/shared/interfaces/agency-node.interface'
import { useCreateAgencyMutation, useUpdateAgencyMutation } from '@saaga/shared/store/api.store'
import { useAppDispatch } from '@saaga/shared/store/store'
import { useCallback, useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import type { AgencyFormSchemaType } from '@/components/agencies/agency-form.schema'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'

import {
  type AgencyMutationFns,
  publishAgencyToOrg,
  saveAgencyDraft,
  submitAgencyForReview,
} from './agency-save-draft.helper'
import { useAgencySaveCallback } from './use-agency-save-callback.hook'

interface UseAgencySaveDraftProps {
  form: UseFormReturn<AgencyFormSchemaType>
  flowDataRef: React.MutableRefObject<{ nodeList: AgentNode[]; edgeList: AgentEdge[] }>
  isEditMode?: boolean
  existingSlug?: string
}

export interface UseAgencySaveDraftReturn {
  isLoading: boolean
  handleSaveDraft: () => Promise<void>
  handlePublishToOrganization: () => Promise<void>
  handleSubmitForReview: () => Promise<void>
}

export const useAgencySaveDraft = ({
  form,
  flowDataRef,
  isEditMode = false,
  existingSlug,
}: UseAgencySaveDraftProps): UseAgencySaveDraftReturn => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()
  const [createAgency, { isLoading: isCreating }] = useCreateAgencyMutation()
  const [updateAgency, { isLoading: isUpdating }] = useUpdateAgencyMutation()

  const fns = useMemo<AgencyMutationFns>(
    () => ({ updateFn: updateAgency, createFn: createAgency }),
    [updateAgency, createAgency]
  )
  const shared = { form, flowDataRef, fns, dispatch, navigate, orgSlug, isEditMode, existingSlug }

  const setVisibility = useCallback(
    (v: AgencyFormSchemaType['visibility']) => form.setValue('visibility', v),
    [form]
  )

  const handleSaveDraft = useAgencySaveCallback({
    ...shared,
    saveFn: saveAgencyDraft,
    errorMessage: 'Please fix the form errors before saving',
  })

  const handlePublishToOrganization = useAgencySaveCallback({
    ...shared,
    saveFn: publishAgencyToOrg,
    errorMessage: 'Please fix the form errors before publishing',
    onSuccess: () => setVisibility('private'),
  })

  const handleSubmitForReview = useAgencySaveCallback({
    ...shared,
    saveFn: submitAgencyForReview,
    errorMessage: 'Please fix the form errors before submitting',
    onSuccess: () => setVisibility('pending_review'),
  })

  return {
    isLoading: isCreating || isUpdating,
    handleSaveDraft,
    handlePublishToOrganization,
    handleSubmitForReview,
  }
}
